import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Settings, Building2, Clock, Shield, Power, AlertTriangle,
  CheckCircle2, XCircle, Wifi, MapPin, Phone, Mail, Calendar,
  Save, RefreshCw, FileText, ExternalLink
} from 'lucide-react';

// Status Badge Component
const StatusBadge = ({ status, label }) => {
  const styles = {
    OK: 'bg-green-100 text-green-700 border-green-200',
    WARNING: 'bg-amber-100 text-amber-700 border-amber-200',
    INCOMPLETE: 'bg-red-100 text-red-700 border-red-200',
    SIMULATION: 'bg-amber-100 text-amber-700 border-amber-200',
    PRODUCTION: 'bg-green-100 text-green-700 border-green-200',
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || styles.OK}`}>
      {label || status}
    </span>
  );
};

// Property Info Section
const PropertyInfoSection = ({ data, onSave }) => {
  const [form, setForm] = useState(data || {});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success('Property info salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'property_name', label: 'Nome Struttura', icon: Building2, required: true },
    { key: 'full_address', label: 'Indirizzo Completo', icon: MapPin, required: true },
    { key: 'city', label: 'Città', icon: MapPin, required: true },
    { key: 'phone', label: 'Telefono', icon: Phone, required: true },
    { key: 'whatsapp_number', label: 'Numero WhatsApp', icon: Phone, required: true },
    { key: 'google_maps_link', label: 'Link Google Maps', icon: ExternalLink, required: true },
    { key: 'checkin_time', label: 'Orario Check-in', icon: Clock, required: true },
    { key: 'checkout_time', label: 'Orario Check-out', icon: Clock, required: true },
    { key: 'wifi_network', label: 'Nome Rete WiFi', icon: Wifi, required: true },
    { key: 'wifi_password', label: 'Password WiFi', icon: Wifi, required: true },
    { key: 'online_checkin_link', label: 'Link Check-in Online', icon: ExternalLink, required: true },
    { key: 'review_link', label: 'Link Recensioni', icon: ExternalLink, required: true },
    { key: 'emergency_contact', label: 'Contatto Emergenze', icon: Phone, required: true },
    { key: 'entry_code', label: 'Codice Ingresso', icon: Shield },
    { key: 'parking_info', label: 'Info Parcheggio', icon: MapPin },
    { key: 'ztl_info', label: 'Info ZTL', icon: AlertTriangle },
    { key: 'timezone', label: 'Timezone', icon: Clock, required: true },
    { key: 'default_language', label: 'Lingua Default', icon: FileText, required: true },
  ];

  const requiredFields = fields.filter(f => f.required);
  const optionalFields = fields.filter(f => !f.required);
  const missingRequired = requiredFields.filter(f => !form[f.key]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Property Information</h3>
            <p className="text-sm text-gray-500">Informazioni struttura per templates</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge 
            status={missingRequired.length === 0 ? 'OK' : 'INCOMPLETE'} 
            label={missingRequired.length === 0 ? 'Completa' : `${missingRequired.length} campi mancanti`}
          />
        </div>
      </div>
      
      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {requiredFields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form[field.key] || ''}
                  onChange={(e) => setForm({...form, [field.key]: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={field.label}
                />
              </div>
            ))}
          </div>
          
          <details className="mt-4">
            <summary className="text-sm text-gray-600 cursor-pointer">Campi opzionali ({optionalFields.length})</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              {optionalFields.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <input
                    type="text"
                    value={form[field.key] || ''}
                    onChange={(e) => setForm({...form, [field.key]: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={field.label}
                  />
                </div>
              ))}
            </div>
          </details>
          
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvataggio...' : 'Salva Property Info'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Quiet Hours Section
const QuietHoursSection = ({ data, onSave }) => {
  const [form, setForm] = useState(data || {
    enabled: true,
    start_hour: 22,
    start_minute: 0,
    end_hour: 8,
    end_minute: 0,
    timezone: 'Europe/Rome',
    allow_urgent_bypass: true
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success('Quiet hours salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Quiet Hours</h3>
            <p className="text-sm text-gray-500">
              {form.enabled 
                ? `${String(form.start_hour).padStart(2,'0')}:${String(form.start_minute).padStart(2,'0')} - ${String(form.end_hour).padStart(2,'0')}:${String(form.end_minute).padStart(2,'0')} (${form.timezone})`
                : 'Disabilitate'}
            </p>
          </div>
        </div>
        <StatusBadge status="OK" label={form.enabled ? 'Attive' : 'Disattive'} />
      </div>
      
      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({...form, enabled: e.target.checked})}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">Abilita Quiet Hours</span>
            </label>
          </div>
          
          {form.enabled && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inizio Ora</label>
                <select
                  value={form.start_hour}
                  onChange={(e) => setForm({...form, start_hour: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  {[...Array(24)].map((_,i) => (
                    <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fine Ora</label>
                <select
                  value={form.end_hour}
                  onChange={(e) => setForm({...form, end_hour: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  {[...Array(24)].map((_,i) => (
                    <option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({...form, timezone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="Europe/Rome">Europe/Rome</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    checked={form.allow_urgent_bypass}
                    onChange={(e) => setForm({...form, allow_urgent_bypass: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-purple-600"
                  />
                  <span className="text-sm text-gray-700">Permetti bypass urgenti</span>
                </label>
              </div>
            </div>
          )}
          
          <div className="flex justify-end pt-4 border-t border-gray-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvataggio...' : 'Salva Quiet Hours'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Global Automation Settings Section  
const GlobalSettingsSection = ({ data, onSave }) => {
  const [form, setForm] = useState(data || {
    scheduler_enabled: true,
    simulation_mode: true,
    auto_send_enabled: false
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const isSafe = form.simulation_mode === true && form.auto_send_enabled === false;

  return (
    <div className={`rounded-xl border shadow-sm ${isSafe ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSafe ? 'bg-amber-100' : 'bg-green-100'}`}>
            <Power className={`w-5 h-5 ${isSafe ? 'text-amber-600' : 'text-green-600'}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Global Automation Switch</h3>
            <p className="text-sm text-gray-600">
              {isSafe ? 'Modalità SIMULAZIONE - nessun messaggio live' : 'Modalità PRODUZIONE - invio automatico attivo'}
            </p>
          </div>
        </div>
        <StatusBadge 
          status={isSafe ? 'SIMULATION' : 'PRODUCTION'} 
          label={isSafe ? 'SAFE MODE' : 'LIVE MODE'}
        />
      </div>
      
      {expanded && (
        <div className="border-t border-gray-200/50 p-4 space-y-4 bg-white/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg border ${form.scheduler_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Power className={`w-4 h-4 ${form.scheduler_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="font-medium text-gray-900">Scheduler</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.scheduler_enabled}
                    onChange={(e) => setForm({...form, scheduler_enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">Engine che processa la coda</p>
            </div>
            
            <div className={`p-4 rounded-lg border ${form.simulation_mode ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-4 h-4 ${form.simulation_mode ? 'text-amber-600' : 'text-green-600'}`} />
                  <span className="font-medium text-gray-900">Simulation Mode</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.simulation_mode}
                    onChange={(e) => setForm({...form, simulation_mode: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">{form.simulation_mode ? 'Messaggi NON inviati' : 'Messaggi inviati realmente'}</p>
            </div>
            
            <div className={`p-4 rounded-lg border ${form.auto_send_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className={`w-4 h-4 ${form.auto_send_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                  <span className="font-medium text-gray-900">Auto Send</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.auto_send_enabled}
                    onChange={(e) => setForm({...form, auto_send_enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">{form.auto_send_enabled ? 'Invio automatico ATTIVO' : 'Solo invio manuale'}</p>
            </div>
          </div>
          
          {!isSafe && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Attenzione: Modalità Produzione</p>
                <p className="text-sm text-red-700 mt-1">
                  Con questa configurazione i messaggi verranno inviati automaticamente agli ospiti reali.
                  Assicurati che tutto sia configurato correttamente prima di salvare.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center pt-4 border-t border-gray-200/50">
            <div className="text-xs text-gray-500">
              Configurazione sicura: Scheduler ON, Simulation ON, Auto Send OFF
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                isSafe 
                  ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvataggio...' : 'Salva Configurazione'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AutomationSettingsPage() {
  const { token } = useAuth();
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [quietHours, setQuietHours] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [propRes, quietRes, globalRes] = await Promise.all([
        fetch(`${API}/admin/automation/property-info`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API}/admin/automation/quiet-hours`, { headers: { Authorization: `Bearer ${token}` }}),
        fetch(`${API}/admin/automation/global-settings`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      if (propRes.ok) {
        const data = await propRes.json();
        setPropertyInfo(data.property_info);
      }
      if (quietRes.ok) {
        const data = await quietRes.json();
        setQuietHours(data.quiet_hours);
      }
      if (globalRes.ok) {
        const data = await globalRes.json();
        setGlobalSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const savePropertyInfo = async (data) => {
    const response = await fetch(`${API}/admin/automation/property-info`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to save');
    fetchData();
  };

  const saveQuietHours = async (data) => {
    const response = await fetch(`${API}/admin/automation/quiet-hours`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to save');
    fetchData();
  };

  const saveGlobalSettings = async (data) => {
    const response = await fetch(`${API}/admin/automation/global-settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to save');
    fetchData();
  };

  return (
    <AdminLayout title="Automation Settings">
      <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Impostazioni Automazione</h1>
            <p className="text-gray-500 mt-1">Phase 5.2A - Configurazione Go-Live</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Ricarica
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Global Settings - Most Important */}
            <GlobalSettingsSection data={globalSettings} onSave={saveGlobalSettings} />
            
            {/* Property Info */}
            <PropertyInfoSection data={propertyInfo} onSave={savePropertyInfo} />
            
            {/* Quiet Hours */}
            <QuietHoursSection data={quietHours} onSave={saveQuietHours} />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
